import { Component, Element, Event, EventEmitter, Listen, Prop, State, h } from '@stencil/core';
import { BielService } from '../../services/biel-service';

@Component({
  tag: 'biel-search',
  styleUrl: 'biel-search.css',
  shadow: true,
})
export class BielSearch {

  private debounce: number = null;

  @Prop() modalPosition: string = 'top-center';
  @Prop() project: string = '';
  @Prop({ mutable: true, reflect: true }) showSearchModal: boolean = false;
  @Prop() fetchData: boolean = true;
  @Prop() email: string = '';
  @Prop() headerTitle: string = 'Company Name AI';
  @Prop() hideFeedback: boolean = false;
  @Prop() hideExpandButton: boolean = false;
  @Prop() errorMessage404: string = "Oops! We could not find the provided project ID.";
  @Prop() errorMessage403: string = "Oops! The request URL does not match the one defined for this project.";
  @Prop() errorMessageDefault: string = "Oops! Please try again later.";
  @Prop() footerText: string = "AI-generated answers may contain errors. Verify official sources before use.";
  @Prop() searchPlaceholder: string = "Search or ask AI...";
  @Prop() hideFilters: boolean = true;
  @Prop() bielId: string = `biel-search-${Math.random().toString(36).substring(2, 9)}`;

  @State() showPoweredBy: boolean = true;
  @State() searchResults: { title: string, fragment: string, url: string }[] = [];
  @State() searchQuery: string = '';
  @State() lastSearchQuery: string = '';
  @State() showModal: boolean = false;
  @State() filters: { position: number, title: string, url: string }[] = [];
  @State() domainFilter: string = '';
  @State() BielBotId: string = `biel-bot-${Math.random().toString(36).substring(2, 9)}`;
  @State() searchId: string = '';

  @Event() messageSent: EventEmitter<{ bielId: string, message: string }>;

  @Element() el: HTMLElement;

  @Listen('closeSearchClicked', { target: 'body' })
  handleSearchClose(event: CustomEvent<{ bielId: string }> | string) {
    let id: string;
    if (typeof event === 'string') {
      id = event;
    } else if (event.detail.bielId) {
      id = event.detail.bielId;
    }

    if (this.bielId === id) {
      const modal = this.el.shadowRoot.querySelector('.biel-search-content') as HTMLElement;
      if (modal) {
        modal.style.animation = 'fadeOut 0.3s';
        setTimeout(() => {
          this.showSearchModal = false;
        }, 299);
      }
    }
  }

  @Listen('bielSearchButtonClick', { target: 'body' })
  handleSearchOpen() {
    this.searchQuery = '';
    this.lastSearchQuery = '';
    this.searchResults = [];
    this.searchId = '';
    this.domainFilter = '';
  }

  @Listen('bielBotButtonClick', { target: 'body' })
  handleSearchButtonClick() {
    const modal = this.el.shadowRoot.querySelector('.biel-search-bot') as HTMLElement;
    if (modal) {
      modal.style.animation = 'fadeOut 0.3s';
      setTimeout(() => {
        document.body.style.overflow = '';
        this.handleBotClose();
      }, 299);
    }
  }

  @Listen('inputChanged', { target: 'body' })
  handleInputChanged(event: CustomEvent<string> | string) {
    if (this.debounce !== null) {
      clearTimeout(this.debounce);
      this.debounce = null;
    }

    const delay = typeof event === 'string' ? 0 : 400 // 0.4s;

    this.debounce = window.setTimeout(() => {
      this.searchQuery = typeof event === 'string' ? event : event.detail;
      this.searchQuery !== '' ? this.sendQuery() : this.searchResults = null;
      this.debounce = null;
    }, delay);
  }

  @Listen('askAiClicked', { target: 'body' })
  handleAskAiClick() {
    this.handleSearchClose(this.bielId);
    setTimeout(() => {
      this.showModal = true;
      if (this.searchQuery.trim()) {
        this.messageSent.emit({bielId: this.BielBotId, message: this.searchQuery});
      }
    }, 300);
  }

  @Listen('closeBot', { target: 'body' })
  handleBotClose() {
    this.showModal = false;
  }

  @Listen('filterSelected', { target: 'body' })
  handleFilterSelected(event: CustomEvent<string>) {
    this.domainFilter = event.detail;
    this.handleInputChanged(this.searchQuery)
  }

  @Listen('resultClicked', { target: 'body' })
  handleResultClicked(event: CustomEvent<{ url: string }>) {
    this.clickResult(event.detail.url);
  }

  modalContent: HTMLElement;
  bielService: BielService;

  componentWillLoad() {
    if (this.fetchData) this.fetchProjectData();
    window.addEventListener('keydown', this.handleKeyPress);
    window.addEventListener('click', this.handleMouseClick);
  }

  async fetchProjectData() {
    this.bielService = new BielService(
      this.project,
      this.errorMessage404,
      this.errorMessage403,
      this.errorMessageDefault
    );
    const result = await this.bielService.getProjectDetails();
    if (typeof result !== 'string') {
      this.showPoweredBy = !result.whitelabel;
      this.filters = result.filters;
    }
  }

  async sendQuery() {
    this.lastSearchQuery = this.searchQuery;
    this.searchResults = await this.bielService.sendQuery(this.searchQuery, this.domainFilter);
  }

  async clickResult(url: string) {
    await this.bielService.searchClicked();
    window.open(url, '_self');
  }

  handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      this.handleSearchClose(this.bielId);
    }
  };

  handleMouseClick = (event) => {
    if (this.showSearchModal) {
      const modal = document.getElementsByTagName("biel-search")[0];
      if (event.target != modal && event.target.parentNode != modal) {
        this.handleSearchClose(this.bielId);
      }
    }
  };

  render() {
    return (
      <div class='biel-search-wrapper'>
        <biel-bot class="biel-search-bot"
          headerTitle={this.headerTitle}
          project={this.project}
          email={this.email}
          modalPosition={this.modalPosition}
          showModal={this.showModal}
          errorMessage404={this.errorMessage404}
          errorMessage403={this.errorMessage403}
          errorMessageDefault={this.errorMessageDefault}
          footerText={this.footerText}
          hideExpandButton={this.hideExpandButton}
          hideFeedback={this.hideFeedback}
          bielId={this.BielBotId}>
        </biel-bot>
        {this.showSearchModal && (
          <div class={`biel-search-content biel-search-content--${this.modalPosition} ${this.showSearchModal ? 'biel-search-content--open' : ''}`} ref={el => (this.modalContent = el)}>
            <div class="biel-search-header">
              <biel-search-header placeholderText={this.searchPlaceholder} hideFilters={this.hideFilters} filters={this.filters} bielId={this.bielId} searchQuery={this.searchQuery}></biel-search-header>
            </div>
            <div class={`biel-search-body ${this.searchQuery !== '' ? 'results' : ''}`}>
              <biel-search-body searchResults={this.searchResults} searchQuery={this.searchQuery} lastSearchQuery={this.lastSearchQuery}></biel-search-body>
            </div>
            <div class="biel-search-footer">
              <div class="biel-search-footer__quick-icons-container">
                <div class="biel-search-footer__quick-icon">Navigate
                  <span class="biel-search-footer__quick-icon-command-key">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-up"><path d="m5 12 7-7 7 7" /><path d="M12 19V5" /></svg>
                  </span>
                  <span class="biel-search-footer__quick-icon-command-key">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down"><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></svg>
                  </span>
                </div>
                <div class="biel-search-footer__quick-icon">Select
                  <span class="biel-search-footer__quick-icon-command-key">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-corner-down-left"><polyline points="9 10 4 15 9 20" /><path d="M20 4v7a4 4 0 0 1-4 4H4" /></svg>
                  </span>
                </div>
                <div class="biel-search-footer__quick-icon">Close
                  <span class="biel-search-footer__quick-icon-command-key">Esc</span>
                </div>
              </div>
              <div class="biel-search-footer__powered-by">
                {this.showPoweredBy && (
                  <span>Powered by <a target="_blank" href="https://biel.ai">biel.ai</a></span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
